import { separate } from '../utils/strings/separate';
import { SupportedCompaniesTypes, SupportedCompanyPlatforms } from './types';

/**
 * Class Platform
 * @package Wooter
 * @author Dumitrana alinus
 * @date 12 Nov, 2020 12:23
 */
export class Platform {
    public static SEPARATOR = '::';

    public company: SupportedCompaniesTypes | string;
    public platform: SupportedCompanyPlatforms | string;
    public flags: string[] = [];
    public namespace: string;

    /**
     * Platform constructor.
     * @param namespace
     */
    constructor(namespace = '') {
        this.setNamespace(namespace);
    }

    /**
     * Get only company and platform
     * const platform = new Platform("covcare::marketplace::vendor");
     * platform.getMain(); // covcare::marketplace
     * @return string
     */
    public getMain(): string {
        return this.join(false);
    }

    /**
     * Create namespace based on company, platform and (or not) flags
     * @param  withFlags
     * @return string
     */
    private join(withFlags = true): string {
        let segments = [this.company, this.platform];
        if (withFlags) {
            segments = segments.concat(this.flags);
        }

        return segments.join(Platform.SEPARATOR).toLowerCase();
    }

    /**
     * Get company of the namespace (1st segment)
     * @return mixed
     * @example
     * const platform = new Platform("covcare::marketplace::vendor");
     * platform.getCompany(); // covcare
     */
    public getCompany(): SupportedCompaniesTypes | string {
        return this.company;
    }

    /**
     * Set the company field
     * @param name
     */
    public setCompany(name: SupportedCompaniesTypes | string) {
        this.company = name;
        this.clearNamespace();
        return this;
    }

    /**
     * Method used to clear namespace to let object recreate it based on properties
     */
    private clearNamespace(): void {
        this.namespace = '';
    }

    /**
     * Get platform of the namespace (2nd segment)
     * @return mixed
     * @example
     * const platform = new Platform("covcare::marketplace::vendor");
     * platform.getPlatform(); // marketplace
     */
    public getPlatform(): SupportedCompanyPlatforms | string {
        return this.platform;
    }

    /**
     * Set the platform field
     * @param name
     */
    public setPlatform(name: SupportedCompanyPlatforms | string) {
        this.platform = name;
        this.clearNamespace();
        return this;
    }

    /**
     * Get flags of the namespace in order specified in the tag
     * @return array
     * @example
     * const platform = new Platform("company::platform::master::of::the::world");
     * platform.getFlags(); // ["master", "of", "the", "world"]
     */
    public getFlags(): string[] {
        return this.flags;
    }

    /**
     * Check if the platform has flag
     *
     * @return bool
     * @example
     * const platform = new Platform("covcare::marketplace::vendor");
     * platform.hasFlag("vendor"); // true
     * platform.hasFlag("customer"); // false
     * @param flag
     */
    public hasFlag(flag: string): boolean {
        return this.flags.includes(flag);
    }

    /**
     * Add flag
     * @param flag
     */
    public addFlag(flag): void {
        this.flags = [...this.flags, flag];
        this.clearNamespace();
    }

    /**
     * Set flags
     * @param flag
     */
    public setFlags(flag) {
        this.flags = flag;
        this.clearNamespace();
        return this;
    }

    /**
     * To string method
     * @return string
     */
    public toString(): string {
        return this.namespace || this.getNamespace();
    }

    /**
     * Get namespace
     * @return string
     */
    public getNamespace(): string {
        if (!this.namespace) {
            this.namespace = this.join();
        }

        return this.namespace;
    }

    /**
     * Set namespace avoiding unwanted uppercase letters
     * @param namespace
     */
    public setNamespace(namespace: string): void {
        this.namespace = `${namespace || ''}`.toLowerCase();
        this.parse();
    }

    /**
     * Determine if the namespace it's like the given value
     * @param value
     * @return bool
     * @example
     * const platform = new Platform("covcare::marketplace::vendor");
     * platform.isLike("covcare"); // true
     * platform.isLike("covcare::marketplace"); // true
     * platform.isLike("covcare::marketplace::vendor"); // true
     * platform.isLike("covcare::marketplace::vendor::start"); // false
     * platform.isLike("apparel"); // false
     * platform.isLike("apparel::order"); // false
     *
     */
    public isLike(value): boolean {
        return this.getNamespace().startsWith(value) || this.getCompany() === value || this.getPlatform() === value;
    }

    /**
     * Parse namespace and fill the tags
     */
    private parse(): void {
        const segments = this.split(this.namespace);

        this.company = segments[0] ?? '';
        this.platform = segments[1] ?? '';
        this.flags = segments.slice(2);
    }

    /**
     * Used to split namespace in that way we correct things like
     * - unwanted spaces
     * - unwanted uppercase letters
     * @param value
     * @return array
     */
    private split(value: string): string[] {
        return separate(`${value || ''}`.toLowerCase(), Platform.SEPARATOR);
    }

    /**
     * Make sure that a value it's everytime a Platform
     * @param platform
     */
    public static from(platform: string | Platform) {
        if (!platform) {
            return null;
        }

        if (platform instanceof Platform) {
            return platform;
        }

        return new Platform(platform);
    }
}
